<template>
  <div>
    <pt-collapsible-sidebar
      ref="collapsibleSidebarRight"
      :width="1080"
      :overlay="true"
      :partial-collapse="false"
      :close-on-outside-click="false"
      position="right"
      class="holiday-sidebar"
    >
      <div slot="expanded" class="h-screen">
        <div class="mx-5">
          <header class="border-b flex flex-row items-center justify-between py-4">
            <div class="flex flex-row items-center">
              <span class="cursor-pointer mt-1" @click="closeSidebar">
                <i class="fa fa-arrow-left mr-4 mb-1" aria-hidden="true" />
              </span>
              <h3 class="ml-2">{{ isEdit ? 'Edit Holiday' : 'Add New Holiday' }}</h3>
            </div>
            <div>
              <pt-button
                :disabled="isLoading"
                class="ml-1 secondary-outline-button secondary-outline-button-dark-grey"
                variant="secondary-outline"
                size="sm"
                @action="showLearnMoreModal"
              >
                Learn More
              </pt-button>
            </div>
          </header>

        </div>
        <div class="flex flex-col justify-between h-auto mt-4">
          <div class="flex flex-col add-edit-schedule-scrollable-container">
            <holidays-list
              :holidays-list="holidaysList"
              :is-edit="isEdit"
              class="mb-8 mx-1 add-edit-schedule-scrollable-content"
              @updateHolidays="updateHolidays"
              @deleteHoliday="deleteHoliday"
            />
          </div>
          <div>
            <div class="mx-5">
              <p class="h-10 border-b" />
            </div>
            <div class="mx-5 mt-2 py-4">
              <pt-button
                :disabled="isLoading"
                :class="['primary-button holiday-save-button', saveButtonStyle]"
                variant="primary"
                size="sm"
                @action="showSubmitModal"
              >
                {{ isEdit ? 'Request Changes' : 'Send Request' }}
              </pt-button>
              <pt-button
                :disabled="isLoading"
                class="ml-1 secondary-outline-button secondary-outline-button-dark-grey"
                variant="secondary-outline"
                size="sm"
                @action="closeSidebar"
              >
                <p class="sidebar-cancel-button">Cancel</p>
              </pt-button>
            </div>
          </div>
        </div>
      </div>
    </pt-collapsible-sidebar>
    <response-modal
      ref="responseModal"
      :is-learn-more="isLearnMore"
      :is-confirm="isConfirm"
      :response="response"
      @onConfirm="onSubmit"
      @onCloseModal="closeResponseModal"
    />
  </div>
</template>

<script>
import HolidaysList from '@components/holidays/list';
import apiRequest from '@src/utils/apiRequest';
import { BULK_CREATE_SPECIAL_HOLIDAYS, UPDATE_SPECIAL_HOLIDAYS_BY_ID } from '@src/config/apiEndpoints';
import ResponseModal from '@components/common/responseModal.vue';
import isEmpty from 'lodash/isEmpty';
import { RESPONSE_MODAL_DATA } from '@constants';
import capitalizeFirstLetter from '@utils/capitalize-first-letter';

export default {
  name: 'HolidayAddEdit',
  components: {
    HolidaysList,
    ResponseModal,
  },
  props: {
    holidayDetails: {
      type: Object,
      default: () => {},
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isLoading: false,
      holidaysList: [],
      isLearnMore: false,
      isConfirm: false,
      response: null,
    };
  },
  computed: {
    saveButtonStyle() {
      return this.isLoading ? 'primary-button-disabled' : 'primary-button-enabled';
    },
  },
  watch: {
    holidayDetails: {
      handler(newVal, oldVal) {
        if (this.holidayDetails) {
          this.holidaysList = [
            this.holidayDetails,
          ];
        }
      },
      deep: true,
    },
  },
  methods: {
    resetData() {
      this.holidaysList = [];
    },
    resetModalData() {
      this.isLearnMore = false;
      this.isConfirm = false;
      this.response = null;
    },
    toggle() {
      this.$refs.collapsibleSidebarRight.toggle();
    },
    closeSidebar() {
      this.resetData();
      this.$emit('closedAddEditSidebar');
      this.toggle();
    },
    showLearnMoreModal() {
      this.$sendEvent('RS_LearnMoreHoliday');
      this.isLearnMore = true;
      this.$refs.responseModal.showModal();
    },
    showSubmitModal() {
      this.$sendEvent('RS_SendHolidayRequest');
      this.resetModalData();
      if (this.isEdit) {
        this.onSubmit();
        return;
      }
      this.isConfirm = true;
      this.$refs.responseModal.showModal();
    },
    closeResponseModal() {
      this.resetModalData();
    },
    async onSubmit() {
      this.$sendEvent('RS_ConfirmHolidayRequest');
      this.isLoading = true;
      const payloadHolidays = [];
      let isSubmitAllowed = true;
      this.holidaysList.forEach((item) => {
        const { id, ...holidayData } = item;
        if (isEmpty(holidayData.name) ||
        isEmpty(holidayData.start_date) ||
        isEmpty(holidayData.end_date) ||
        (!holidayData.is_full_day_closed && (isEmpty(holidayData.opening_time) || isEmpty(holidayData.closing_time)))) {
          isSubmitAllowed = false;
          return;
        }
        payloadHolidays.push({
          ...holidayData,
        });
      });
      if (!isSubmitAllowed) {
        this.isLoading = false;
        this.$notification.error('Please fill up all the required fields!');
        return;
      }
      const requestConfig = {
        url: BULK_CREATE_SPECIAL_HOLIDAYS,
        method: 'POST',
        data: payloadHolidays,
      };
      if (this.isEdit) {
        const holidayId = this.holidaysList[0].id;
        requestConfig.url = UPDATE_SPECIAL_HOLIDAYS_BY_ID.replace(':id', holidayId);
        requestConfig.method = 'PUT';
        requestConfig.data = payloadHolidays[0];
      }
      try {
        const response = await apiRequest(requestConfig);
        this.isLoading = false;
        this.closeSidebar();
        this.$emit('fetchHolidaysList');
        if (!(response && response.data && response.data.message)) {
          this.$notification.success('Succesfully created Special Holidays request');
          return;
        }
        this.$notification.success(capitalizeFirstLetter(response.data.message));
        if (response.data.code && response.data.code in RESPONSE_MODAL_DATA) {
          this.response = response;
          this.$refs.responseModal.showModal();
        }
      } catch (error) {
        this.isLoading = false;
        if (!(error && error.response && error.response.data && error.response.data.message)) {
          this.$notification.error('Failed to create Special Holidays request');
          return;
        }
        this.$notification.error(capitalizeFirstLetter(error.response.data.message));
        if (error.response.data.code && error.response.data.code in RESPONSE_MODAL_DATA) {
          this.response = error.response;
          this.$refs.responseModal.showModal();
        }
      }
    },
    updateHolidays(holidaysList) {
      this.holidaysList = holidaysList;
    },
    deleteHoliday(index) {
      this.holidaysList.splice(index, 1);
    },
  },
};
</script>
